import type { AssessmentReport, ReadinessBand, ReportPillar } from '../api/reportApi';
import { Check } from 'lucide-react';
import { ActionPlanCard } from './ActionPlanCard';

const GREEN = '#026857';
const AMBER = '#f3c66c';

const REASSESS_BENEFITS = [
  'Measure your progress across all six readiness pillars.',
  "Celebrate the planning you've completed.",
  'Identify new planning opportunities as life changes.',
  'Update your Personalized Action Plan.',
  'Ensure your financial plan continues to support the life you want to live.',
];

const CYCLE_STEPS = [
  { n: '01', t: 'Assess', d: 'Assess your readiness.' },
  { n: '02', t: 'Plan', d: 'Plan with confidence.' },
  { n: '03', t: 'Activate', d: 'Activate the right resources and support.' },
  {
    n: '04',
    t: 'Repeat',
    d: 'Repeat annually to keep your longevity and financial plans aligned as life evolves.',
  },
];

function Gauge({ score, onDark = false }: { score: number; onDark?: boolean }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.max(0, Math.min(100, score)) / 100);
  const track = onDark ? 'rgba(255,255,255,0.18)' : '#ece6d9';
  const arc = onDark ? AMBER : GREEN;
  const numColor = onDark ? '#ffffff' : '#111827';
  const subColor = onDark ? 'rgba(246,231,196,0.7)' : '#9ca3af';
  return (
    <svg width="140" height="140" viewBox="0 0 140 140" role="img" aria-label={`Score ${score} of 100`}>
      <circle cx="70" cy="70" r={r} fill="none" stroke={track} strokeWidth="12" />
      <circle
        cx="70"
        cy="70"
        r={r}
        fill="none"
        stroke={arc}
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        transform="rotate(-90 70 70)"
      />
      <text x="70" y="70" textAnchor="middle" style={{ font: '600 34px serif', fill: numColor }}>
        {score}
      </text>
      <text x="70" y="90" textAnchor="middle" style={{ font: '400 11px sans-serif', fill: subColor }}>
        out of 100
      </text>
    </svg>
  );
}

function PillarBar({ pillar }: { pillar: ReportPillar }) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-sm">
        <span className="text-gray-800">{pillar.name}</span>
        <span className="text-gray-500">{pillar.pct}%</span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full"
          style={{ width: `${pillar.pct}%`, background: pillar.isGap ? AMBER : GREEN }}
        />
      </div>
    </div>
  );
}

function Quote({ label, text }: { label: string; text: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#026857]">{label}</div>
      <p className="mt-1 text-[15px] leading-relaxed text-gray-700">&ldquo;{text}&rdquo;</p>
    </div>
  );
}

/**
 * Horizontal readiness band bar: the four bands as proportional segments (worst →
 * best, left → right), the current one filled green, with a marker at the score.
 * Segment widths + the score marker share the 0–100 scale, so they stay in sync
 * with the bounds the backend derives from the scoring thresholds.
 */
function ReadinessBar({
  bands,
  score,
  currentCategory,
}: {
  bands: ReadinessBand[];
  score: number;
  currentCategory: string;
}) {
  const ordered = [...bands].sort((a, b) => a.min - b.min);
  const markerLeft = Math.max(0, Math.min(100, score));
  // Keep the marker + its label inside the bar at the extremes: half-width shift
  // in the middle, but flush-left at 0 and flush-right at 100 (no edge overflow).
  const markerShift =
    markerLeft <= 0 ? 'translateX(0)' : markerLeft >= 100 ? 'translateX(-100%)' : 'translateX(-50%)';
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6">
      <div className="mb-7 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        Readiness bands
      </div>
      <div className="relative">
        <div
          className="absolute -top-6 text-sm font-semibold text-gray-900"
          style={{ left: `${markerLeft}%`, transform: markerShift }}
        >
          {score}
        </div>
        <div
          className="absolute -top-1 z-10 h-5 w-0.5 bg-gray-900"
          style={{ left: `${markerLeft}%`, transform: markerShift }}
        />
        <div className="flex h-3 gap-1">
          {ordered.map((band) => (
            <div
              key={band.category}
              className={`h-full rounded-full ${
                band.category === currentCategory ? 'bg-[#026857]' : 'bg-[#ece6d9]'
              }`}
              style={{ width: `${band.max - band.min}%` }}
            />
          ))}
        </div>
      </div>
      <div className="mt-3 flex gap-1">
        {ordered.map((band) => {
          const isCurrent = band.category === currentCategory;
          // Match each label's width to its segment (same gap) so the legend lines
          // up under the bands instead of splitting into equal quarters.
          return (
            <div
              key={band.category}
              className="min-w-0"
              style={{ width: `${band.max - band.min}%` }}
            >
              <div
                className={`text-[13px] leading-tight ${
                  isCurrent ? 'font-semibold text-[#026857]' : 'text-gray-500'
                }`}
              >
                {band.label}
              </div>
              <div className={`mt-0.5 text-[11px] ${isCurrent ? 'text-[#026857]' : 'text-gray-400'}`}>
                {band.range}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function ReportView({ report }: { report: AssessmentReport }) {
  const firstName = report.clientName.split(' ')[0] || report.clientName;
  // Perspective-aware copy: the report is reachable by the client (their own) and
  // the advisor (when shared). Address the client directly; name the client for
  // the advisor so the labels never read as advisor-owned.
  const isSelf = report.viewerIsClient;
  const visionLabel = isSelf ? 'Your vision' : `${firstName}'s vision`;
  const concernLabel = isSelf ? 'What concerns you' : `What concerns ${firstName}`;
  // Recommendations are the gap pillars ranked worst-first (same order as scoring),
  // so they drive both the Top Opportunities summary and the action-plan cards.
  const opportunities = report.recommendations;
  const hasQuotes = Boolean(report.vision || report.concern);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-8">
      {/* Overall readiness (green) + vision / concern */}
      <section className={`grid gap-4 ${hasQuotes ? 'lg:grid-cols-[minmax(0,320px)_1fr]' : ''}`}>
        <div className="rounded-2xl bg-gradient-to-br from-[#04332b] via-[#0d5d4e] to-[#1a7d6c] p-8 text-center">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#f6e7c4]/60">
            Overall Readiness
          </div>
          <div className="mt-3 flex justify-center">
            <Gauge score={report.overallScore} onDark />
          </div>
          <h1 className="mt-4 font-serif text-2xl text-white">{report.categoryLabel}</h1>
          <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-[#f6e7c4]/85">
            {report.categoryDescription}
          </p>
        </div>
        {hasQuotes && (
          <div className="flex flex-col gap-4">
            {report.vision && <Quote label={visionLabel} text={report.vision} />}
            {report.concern && <Quote label={concernLabel} text={report.concern} />}
          </div>
        )}
      </section>

      {/* Readiness bands (horizontal) */}
      <ReadinessBar
        bands={report.readinessBands}
        score={report.overallScore}
        currentCategory={report.category}
      />

      {/* Six-pillar dashboard */}
      <section className="rounded-2xl border border-gray-200 bg-white p-6">
        <h2 className="mb-1 font-serif text-xl text-gray-900">Six pillars of readiness</h2>
        <p className="mb-4 text-sm text-gray-500">
          The lowest-scoring pillars (highlighted) are where focused attention moves the needle most.
        </p>
        <div className="space-y-4">
          {report.pillars.map((p) => (
            <PillarBar key={p.key} pillar={p} />
          ))}
        </div>
      </section>

      {/* Your Top Opportunities (3 columns) */}
      {opportunities.length > 0 && (
        <section className="rounded-2xl border border-gray-200 bg-white p-6">
          <h2 className="font-serif text-xl text-gray-900">Your top opportunities</h2>
          <p className="mb-4 mt-1 text-sm text-gray-500">
            The lowest-scoring pillars — where focused attention will move the needle most.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            {opportunities.map((rec) => (
              <div
                key={rec.pillarKey}
                className="flex items-center gap-3 rounded-xl border border-l-4 border-gray-200 border-l-[#f3c66c] bg-[#fbf9f5] p-4"
              >
                <span className="font-serif text-xl font-semibold text-[#f3c66c]">{rec.rank}</span>
                <div>
                  <div className="font-serif text-[15px] font-semibold text-gray-900">{rec.name}</div>
                  <div className="text-xs text-gray-500">Currently {rec.pctLabel}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Action plans — one full card per opportunity */}
      {opportunities.length > 0 && (
        <section className="space-y-6">
          <h2 className="font-serif text-xl text-gray-900">Your personalized action plan</h2>
          {opportunities.map((rec) => (
            <ActionPlanCard key={rec.pillarKey} rec={rec} />
          ))}
        </section>
      )}

      {/* Closing: reassess cadence + review with advisor + planning cycle */}
      <section className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
        <h2 className="font-serif text-2xl text-gray-900">
          Your longevity journey doesn&rsquo;t end here
        </h2>
        <p className="mt-2.5 text-[15px] leading-relaxed text-gray-600">
          Preparing for a longer life isn&rsquo;t a one-time event — it&rsquo;s an ongoing planning
          process. As your life evolves, so will your goals, relationships, health, support system,
          and financial priorities. That&rsquo;s why your Longevity Readiness Assessment is
          designed to be revisited over time.
        </p>

        <h3 className="mt-7 font-serif text-lg text-gray-900">We recommend you reassess annually</h3>
        <ul className="mt-3.5 space-y-2.5">
          {REASSESS_BENEFITS.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-[14px] leading-snug text-gray-800">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#026857]" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 rounded-xl border border-[#026857]/15 bg-[#eaf1ee] p-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#026857]">
            Review your results with your financial advisor
          </div>
          <p className="mt-2 text-[14px] leading-relaxed text-gray-600">
            Each annual assessment creates an opportunity to revisit your longevity plan together —
            reviewing changes in your goals, housing preferences, support network, healthcare needs,
            and financial priorities. The best financial plans aren&rsquo;t static; they evolve as
            life evolves.
          </p>
        </div>

        <div className="mt-8 rounded-2xl bg-gradient-to-br from-[#04332b] via-[#0d5d4e] to-[#1a7d6c] p-6 text-[#f6e7c4] sm:p-8">
          <div className="text-[10.5px] font-semibold uppercase tracking-widest text-[#f6e7c4]/70">
            The bQuest Longevity Planning Cycle
          </div>
          <div className="mb-6 mt-1.5 font-serif text-2xl text-white">
            Assess. Plan. Activate. Repeat.
          </div>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
            {CYCLE_STEPS.map((c) => (
              <div key={c.n}>
                <div className="font-mono text-[13px] text-[#f3c66c]">{c.n}</div>
                <div className="mt-1.5 text-[14px] font-semibold text-white">{c.t}</div>
                <div className="mt-1 text-[13px] leading-snug text-[#f6e7c4]/80">{c.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
